import {
  t,
  useWealthPlan,
  selectors,
  getFlagsSelector,
  config,
} from '@formue-app/core';

import { unresolvedTasksSelector } from '../store/ui/todo/selectors';
import { extendedReportingRoutes } from './extendedReporting';

import {
  SubMenuPage,
  AuthCallbackPage,
  AuthSsoPage,
  LoginPage,
  HomePage,
  WealthPlanPage,
  InvestmentProfilePage,
  InvestmentStrategiesPage,
  InvestmentStrategyPage,
  InvestmentSimulatorPage,
  StrategiesPage,
  ExpertInputPage,
  FamilyAndOwnershipPage,
  GoalsPage,
  PortfolioPage,
  PortfolioOverviewPage,
  PortfolioStrategyPage,
  PortfolioConstructionPage,
  HoldingsPage,
  HoldingsOverviewPage,
  FundsPage,
  TransactionsPage,
  DocumentsPage,
  TodoPage,
  TodoItemPage,
  AccountTeamPage,
  ContactPage,
  WealthBalancePage,
  ClientsPage,
  TaxInfoGeneralBasePage,
  TaxReportPageBasePage,
  NotFoundPage,
  FactSheetsPage,
  LensesPage,
  SustainabilityPage,
  GeographyPage,
  SectorsPage,
  CostReportPage,
  ProfileOverviewPage,
  SelfDeclarationPage,
  FundsListPage,
  CompaniesListPage,
  AcccountSettingsPage,
  ApiClientPage,
  NewApiClientPage,
  PresentationPage,
  EventsPage,
  EventPage,
  WebinarPage,
  WebinarsPage,
  PodcastPage,
  MeetingsPage,
  MeetingPage,
  AnnualReviewReportPage,
  StrategyDetailsPage,
  StrategySimulatorPage,
  ExtendedReportingPage,
  ExtendedReportingOverviewPage,
  AdvisorsCorner,
  AdvisorsCornerOverview,
  StrategyDetailsPreferencesPage,
  StrategyDetailsProductsPage,
  StrategyDetailsCostPage,
  StrategyDetailsAssetAllocationPage,
  StrategyDetailsImplementationPage,
  StrategyDetailsCommentsPage,
  ReportingPage,
  PeriodicReportingPage,
  RegistrationPage,
  PeriodicReportingOverviewPage,
  PeriodicReportingExportPage,
  AlternativeAssetsReportingExportPage,
  TransactionsReportingPage,
  AlternativeAssetsReportingPage,
  AiActionsDevPage,
  // Route pages are code-split — see lazyPages.js. The menu metadata on each
  // route object (title/icon/…) stays eager; only the page component is lazy.
} from './lazyPages';
import { useMemo } from 'react';
import { accent } from '../constants/colors';
import { useSelector } from 'react-redux';

const { operatingCountrySelector } = selectors.auth;
const { upcomingMeetingsCountSelector } = selectors.entities.meetings;

/**
 * Who may manage API clients: customers with the apiAccess feature, and an
 * employee switched to their own client user, who manages that customer's
 * clients. The API refuses leads, an employee acting as the employee, and an
 * employee impersonating anyone else. `isEmployeeClientUser` alone is not
 * enough: it is also true once an employee stops impersonating. Also gates the
 * section on the profile page.
 */
export const canManageApiClients = ({
  apiAccess,
  isLead,
  isEmployee,
  isImpersonating,
  isEmployeeClientUser,
}) =>
  Boolean(apiAccess) &&
  !isLead &&
  (!isEmployee || (isImpersonating && isEmployeeClientUser));

/**
 * The route table: every route the app has, in one nested list.
 *
 * Whether a route *exists for a given user* is written on the route itself, as
 * a `when` predicate over the user's flags — `pruneRoutes` below applies them.
 * The other fields the flags feed (showInMenu, badge selectors) are cosmetic
 * and computed here from the same flags.
 *
 * Two consumers:
 *  - `useRoutes()` below prunes it with the user's real flags from redux. This
 *    is what the menus use, and what decides what a user may reach.
 *  - `routeSuperset` below keeps every predicated route, producing the static
 *    tree the router matches URLs against. React Router v7 wants that tree
 *    created once, outside React, so it must cover every user — access is
 *    enforced per render instead (see components/router/RouteElement).
 */
const buildRouteTable = ({
  showWealthPlan,
  isImpersonating,
  alternativeAssetsOverview,
} = {}) => {
  // prettier-ignore
  return [
    { path: '/advisor', component: AdvisorsCorner, title: t('app:menu:menuItems:advisor'), icon: 'Decision', showInMenu: false, showInAdvisorMenu: true, when: ({ isEmployee }) => isEmployee, routes: [
      { path: '/advisor', component: AdvisorsCornerOverview, title: t('app:menu:menuItems:advisor'), icon: 'Decision', exact: true, showInMenu: isImpersonating },
      { path: '/advisor/presentation/:id', component: PresentationPage, showInMenu: false, exact: true },
      { path: '/advisor/clients', component: ClientsPage, title: t('app:impersonation:title'), icon: 'Search', showInMenu: true, exact: true },
      { path: '/advisor/strategy-simulator', component: StrategySimulatorPage, title: t('app:advisor:strategySimulator:title'), showInMenu: false, exact: true },
      { path: '/advisor/strategy-simulator/details/:id', component: StrategyDetailsPage, showInMenu: false, routes: [
        { path: '/advisor/strategy-simulator/details/:id', component: StrategyDetailsAssetAllocationPage, title: t('app:advisor:strategySimulator:assetAllocation:title'), exact: true },
        { path: '/advisor/strategy-simulator/details/:id/preferences', component: StrategyDetailsPreferencesPage, title: t('app:advisor:strategySimulator:preferences:title'), exact: true },
        { path: '/advisor/strategy-simulator/details/:id/product', component: StrategyDetailsProductsPage, title: t('app:advisor:strategySimulator:product:title'), exact: true },
        { path: '/advisor/strategy-simulator/details/:id/implementation', component: StrategyDetailsImplementationPage, title: t('app:advisor:strategySimulator:implementation:title'), exact: true },
        { path: '/advisor/strategy-simulator/details/:id/comments', component: StrategyDetailsCommentsPage, title: t('app:advisor:strategySimulator:comments:title'), exact: true },
        { path: '/advisor/strategy-simulator/details/:id/cost', component: StrategyDetailsCostPage, title: t('app:advisor:strategySimulator:cost:title'), exact: true, showInMenu: true },
      ]},
      ]
    },
    { path: '/auth/callback', component: AuthCallbackPage, exact: true, public: true, showInMenu: false },
    { path: '/auth/sso', component: AuthSsoPage, exact: true, public: true, showInMenu: false },
    { path: '/login', component: LoginPage, exact: true, public: true, showInMenu: false },
    { path: '/registration', component: RegistrationPage, public: true, exact: true, showInMenu: false },
    ...(config.dev ? [{ path: '/__ai-actions', component: AiActionsDevPage, exact: true, showInMenu: false }] : []),
    { path: '/', component: HomePage, title: t('app:home:title'), exact: true, icon: 'House' },
    { path: '/events', component: EventsPage, title: 'Events', exact: true,  icon: 'Event', showInMenu: false, },
    { path: '/events/:id', component: EventPage, title: 'Event', exact: true,  icon: 'Event', showInMenu: false },
    { path: '/webinars', component: WebinarsPage, title: 'Webinar', exact: true,  icon: 'Art', showInMenu: false },
    { path: '/webinars/:id', component: WebinarPage, title: 'Webinar', exact: true,  icon: 'Art', showInMenu: false },
    { path: '/podcasts', component: PodcastPage, title: 'Podcast', exact: true,  icon: 'Event', showInMenu: false },
    { path: '/todo', component: TodoPage, title: t('app:todo:title'), icon: 'To Do', selector: unresolvedTasksSelector },
    { path: '/todo/:type/:id', component: TodoItemPage, showInMenu: false },
    { path: '/wealth-plan', component: SubMenuPage, title: t('app:menu:wealthPlanTitle'), showInMenu: showWealthPlan, icon: 'Strategy', routes: [
        { path: '/wealth-plan', component: WealthPlanPage, title: t('app:globals:overview'), exact: true },
        { path: '/wealth-plan/goals', component: GoalsPage, showMobileTitle: false, inverted: true, title: t('app:wealthPlan:goals:title'), exact: true },
        { path: '/wealth-plan/family-and-ownership', component: FamilyAndOwnershipPage, showMobileTitle: false, inverted: true, title: t('app:wealthPlan:familyAndOwnership:title'), exact: true },
        { path: '/wealth-plan/wealth-balance', component: WealthBalancePage, showMobileTitle: false, inverted: true, title: t('app:wealthBalance:title'), exact: true },
        { path: '/wealth-plan/investment-profile', component: InvestmentProfilePage, showMobileTitle: false, inverted: true, title: t('app:wealthPlan:investmentProfile:title'), exact: true },
        {
          path: '/wealth-plan/investment-strategies', component: InvestmentStrategiesPage, showMobileTitle: false, title: t('app:wealthPlan:investmentStrategy:title'), routes: [
            { path: '/wealth-plan/investment-strategies/strategies', component: StrategiesPage, showMobileTitle: false, inverted: true, title: t('app:wealthPlan:investmentStrategy:navigation:title'), exact: true },
            { path: '/wealth-plan/investment-strategies/investment-simulator', component: InvestmentSimulatorPage, showMobileTitle: false, inverted: true, title: t('app:wealthPlan:investmentSimulator:title'), exact: true, when: () => false },
          ]
        },
        { path: '/wealth-plan/expert-input', component: ExpertInputPage, showMobileTitle: false, inverted: true, title: t('app:wealthPlan:expertAdvice:title'), exact: true },
      ]
    },
    { path: '/investment-strategy/:id', component: InvestmentStrategyPage, showInMenu: false },
    { path: '/portfolio/periodic-reporting/export', component: PeriodicReportingExportPage, title: 'Export', exact: true, showInMenu: false },
    { path: '/portfolio/periodic-reporting/alternative-assets-reporting/export', component: AlternativeAssetsReportingExportPage, title: 'Alternative assets export', exact: true, showInMenu: false },
    {
      path: '/portfolio', component: PortfolioPage, title: t('app:portfolio:title'), icon: 'Investment', routes: [
        { path: '/portfolio', component: PortfolioOverviewPage, title: t('app:globals:overview'), exact: true },
        { path: '/portfolio/holdings', component: HoldingsPage, title: t('app:portfolio:holdings'), routes: [
            { path: '/portfolio/holdings', component: HoldingsOverviewPage, title: t('app:portfolio:holdings'), exact: true },
            { path: '/portfolio/holdings/securities', component: FundsPage, title: t('app:portfolio:holdings'), exact: true, hideFilter: true }
          ]
        },
        { path: '/portfolio/transactions', component: TransactionsPage, title: t('app:portfolio:transactions'), exact: true },
        { path: '/portfolio/strategy', component: PortfolioStrategyPage, title: t('app:portfolio:strategy'), exact: true, showInMenu: true },
        {
          path: '/portfolio/lenses', component: LensesPage, title: t('app:portfolio:lenses'), routes: [
            { path: '/portfolio/lenses/portfolio-construction', component: PortfolioConstructionPage, title: t('app:portfolio:portfolioConstruction'), exact: true, color: accent.velvet4 },
            { path: '/portfolio/lenses/sustainability', component: SustainabilityPage, title: t('app:esg:title'), exact: true, color: accent.moss4 },
            { path: '/portfolio/lenses/geography', component: GeographyPage, title: t('app:portfolio:geography'), exact: true, color: accent.ocean4},
            { path: '/portfolio/lenses/sectors', component: SectorsPage,title: t('app:portfolio:sectors'), exact: true, color: accent.raspberry4 },
          ]
        },
        { path: '/portfolio/factsheets', component: FactSheetsPage, title: t('app:portfolio:factsheets'), exact: true, hideFilter: true, when: ({ isLead }) => !isLead },
        { path: '/portfolio/periodic-reporting', component: PeriodicReportingOverviewPage, title: t('app:portfolio:periodicReporting:title'), hideHeaderForPrint: true, routes: [
          { path: '/portfolio/periodic-reporting', component: PeriodicReportingPage, title: t('app:portfolio:periodicReporting:title'), icon: 'Investment', exact: true, showInMenu: true, color: accent.ocean4, hideFilter: true, hideHeaderForPrint: true },
          { path: '/portfolio/periodic-reporting/transactions-reporting', component: TransactionsReportingPage, title: t('app:portfolio:transactionsReporting:title'), icon: 'Transactions', exact: true, showInMenu: false, color: accent.ocean4, hideFilter: true, hideHeaderForPrint: true },
          { path: '/portfolio/periodic-reporting/alternative-assets-reporting', component: AlternativeAssetsReportingPage, title: t('app:portfolio:alternativeAssetsReporting:title'), icon: 'Portfolio Report', exact: true, showInMenu: Boolean(alternativeAssetsOverview), color: accent.ocean4, hideFilter: true, hideHeaderForPrint: true },
        ] },
        { path: '/portfolio/reporting', component: ReportingPage, title: t('app:portfolio:reporting:title'), when: ({ isLead }) => !isLead, routes: [
            { path: '/portfolio/reporting/tax/2025', component: TaxInfoGeneralBasePage, title: t('app:portfolio:tax'), icon: 'Taxes', exact: true, color: accent.ocean4, hideFilter: true, when: ({ operatingCountry }) => operatingCountry !== 'dk' },
            { path: '/portfolio/reporting/tax/:year', component: TaxInfoGeneralBasePage, title: t('app:portfolio:tax'), icon: 'Taxes', exact: true, color: accent.ocean4, showInMenu: false, hideFilter: true },
            { path: '/portfolio/reporting/tax/declaration/:year', component: TaxReportPageBasePage, icon: 'Taxes', exact: true, color: accent.ocean4, showInMenu: false, hideFilter: true },
            { path: '/portfolio/reporting/cost', component: CostReportPage, title: t('app:portfolio:cost'), icon: 'Transparency', exact: true, color: accent.ocean4, hideFilter: true },
          ]
        },
      ]
    },
    { path: '/extended-reporting', component: ExtendedReportingPage, title: t('app:portfolio:extendedReporting:overview'), showInMenu: false, routes: [
      { path: '/extended-reporting', component: ExtendedReportingOverviewPage, title: t('app:portfolio:extendedReporting:overview'), exact: true },
      ...extendedReportingRoutes
    ]},
    { path: '/list/funds-list', component: FundsListPage, exact: true, showInMenu: false },
    { path: '/list/companies-list', component: CompaniesListPage, exact: true, showInMenu: false },
    { path: '/meetings', component: MeetingsPage, title: t('app:eventCalendar:meetings:title'), icon: 'Calendar', selector: upcomingMeetingsCountSelector, showInMenu: true, when: ({ ampMeetings }) => ampMeetings },
    { path: '/meetings/:id', component: MeetingPage, showInMenu: false, icon: 'Calendar check', when: ({ ampMeetings }) => ampMeetings },
    { path: '/documents', component: DocumentsPage, title: t('app:documents:title'), icon: 'Document Single', exact: true },
    { path: '/profile', component: ProfileOverviewPage, title: t('app:menu:profile'), icon: 'Individual', exact: true, showInMenu: false },
    { path: '/profile/self-declaration', component: SelfDeclarationPage, title: t('app:profile:selfDeclaration:title'), exact: true, showInMenu: false },
    { path: '/profile/accounts/:id', component: AcccountSettingsPage, icon: 'Individual', exact: true, showInMenu: false },
    { path: '/profile/api-clients/new', component: NewApiClientPage, title: t('app:settings:apiClients:newTitle'), exact: true, showInMenu: false, when: canManageApiClients },
    { path: '/profile/api-clients/:id', component: ApiClientPage, exact: true, showInMenu: false, when: canManageApiClients },

    { path: '/account-team', component: AccountTeamPage, title: t('app:menu:menuItems:clientTeam'), icon: 'Team', showInMenu: false, exact: true },
    { path: '/contact', component: ContactPage, title: t('app:menu:contact'), showInMenu: false, exact: true },
    { path: '/report/:id', component: AnnualReviewReportPage, showInMenu: false, exact: true },

    // 404 handler
    { path: '*', component: NotFoundPage, showInMenu: false }
  ];
};

/**
 * Applies the `when` predicates, recursively: a route survives when it has no
 * predicate or its predicate accepts the flags. `flags === null` keeps every
 * predicated route — that is what builds the superset. The predicate itself is
 * stripped, so it never reaches the menus or the page props.
 */
const pruneRoutes = (routes, flags) =>
  routes
    .filter(({ when }) => !when || flags === null || when(flags))
    .map(({ when, ...route }) =>
      route.routes ? { ...route, routes: pruneRoutes(route.routes, flags) } : route
    );

const createRoutes = (flags) => pruneRoutes(buildRouteTable(flags), flags);

/**
 * The routes this user may actually reach, and the data the menus render from.
 * Unchanged in behaviour from before the v7 migration.
 */
export const useRoutes = () => {
  const showWealthPlan = useWealthPlan();
  const operatingCountry = useSelector(operatingCountrySelector);

  const isImpersonating = useSelector((state) => state.ui.user.isImpersonating);
  const isEmployeeClientUser = useSelector(
    (state) => state.ui.user.isEmployeeClientUser
  );
  const isEmployee = useSelector((state) => state.auth.isEmployee);

  const {
    isLead,
    features: { ampMeetings, alternativeAssetsOverview, apiAccess },
  } = useSelector(getFlagsSelector);

  // `RouteElement` calls this on every matched route render, and the menus call
  // it too, so rebuild the ~74-route table only when something it depends on
  // changes. `language` is a dependency because the route titles go through
  // `t(...)`, so they must refresh when the user switches language.
  const language = useSelector((state) => state.ui.user.language);

  return useMemo(
    () =>
      createRoutes({
        showWealthPlan,
        operatingCountry,
        isImpersonating,
        isEmployeeClientUser,
        isEmployee,
        isLead,
        ampMeetings,
        alternativeAssetsOverview,
        apiAccess,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      showWealthPlan,
      operatingCountry,
      isImpersonating,
      isEmployeeClientUser,
      isEmployee,
      isLead,
      ampMeetings,
      alternativeAssetsOverview,
      apiAccess,
      // Not referenced inside the callback, so exhaustive-deps calls it
      // unnecessary — but `t(...)` resolves against the current language, so
      // without this the menu titles would keep the previous language.
      language,
    ]
  );
};

/**
 * Every route that can exist for any user, built once at module scope: the
 * unpruned table. Only the router's matching tree is built from this — it
 * deliberately says nothing about what the current user is allowed to open.
 * `RouteElement` re-checks each route against `useRoutes()` on render, so a
 * route the user has no access to is still unreachable, including by typing
 * its URL.
 *
 * Built without flags: the only fields flags feed outside `when` predicates
 * are cosmetic (showInMenu and friends), and routeTree.js reads none of them —
 * only `path`, `routes` and `public`.
 */
export const routeSuperset = pruneRoutes(buildRouteTable(), null);
